if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.handleQuantityChange = this.handleQuantityChange.bind(this);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          this.updateTotalPrice();
        }

        this.quantityInput?.addEventListener('change', this.handleQuantityChange);

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.abortController?.abort();
        this.quantityInput?.removeEventListener('change', this.handleQuantityChange);

        if (this.blockPositionHandler) {
          document.removeEventListener('matchMobile', this.blockPositionHandler);
          document.removeEventListener('unmatchMobile', this.blockPositionHandler);
        }

        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        const isFromStickyAtc = event?.detail?.source === 'sticky-atc-bar' || Boolean(event?.target?.closest?.('sticky-atc-bar, .sticky-atc-bar'));
        if (!this.contains(event.target) && !isFromStickyAtc) return;

        this.resetProductFormState();

        const preventMediaScroll = event.detail?.source === 'sticky-atc-bar';
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const getCleanUrl = (u) => (u ? u.split('?')[0].split('#')[0] : '');
        const shouldSwapProduct = this.dataset.url && productUrl && getCleanUrl(this.dataset.url) !== getCleanUrl(productUrl);
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        let selectedVariantId = target?.dataset?.variantId || null;
        if (!selectedVariantId && productUrl) {
          const match = productUrl.match(/[?&]variant=([0-9]+)/);
          if (match) selectedVariantId = match[1];
        }

        let matchedVariant = null;
        const variantSelects = this.querySelector('variant-selects');
        const variantsScript =
          this.querySelector('variant-selects [data-product-variants]') ||
          document.querySelector('[data-product-variants], [data-sticky-atc-variants]');

        if (variantsScript?.textContent) {
          try {
            const allVariants = JSON.parse(variantsScript.textContent);
            let selectedOptions = [];

            if (variantSelects) {
              const groups = variantSelects.querySelectorAll('fieldset, div.product-form__input');
              if (groups.length) {
                groups.forEach((group) => {
                  const checked = group.querySelector('input[type="radio"]:checked');
                  if (checked) {
                    selectedOptions.push(checked.value.trim());
                    return;
                  }
                  const select = group.querySelector('select');
                  if (select && select.value) {
                    selectedOptions.push(select.value.trim());
                  }
                });
              }
              if (!selectedOptions.length) {
                variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
                  selectedOptions.push(r.value.trim());
                });
                if (!selectedOptions.length) {
                  variantSelects.querySelectorAll('select').forEach((sel) => {
                    if (sel.value) selectedOptions.push(sel.value.trim());
                  });
                }
              }
            }

            if (selectedOptions.length) {
              matchedVariant = allVariants.find((v) => {
                if (Array.isArray(v.options) && v.options.length === selectedOptions.length) {
                  return v.options.every((opt, idx) => opt && opt.trim().toLowerCase() === selectedOptions[idx].toLowerCase());
                }
                const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
                if (vOpts.length === selectedOptions.length) {
                  return vOpts.every((opt, idx) => opt && opt.trim().toLowerCase() === selectedOptions[idx].toLowerCase());
                }
                return false;
              });
            }

            if (!matchedVariant && selectedVariantId) {
              matchedVariant = allVariants.find((v) => String(v.id) === String(selectedVariantId));
            }
            if (!matchedVariant && target?.value) {
              const targetVal = String(target.value).trim().toLowerCase();
              matchedVariant = allVariants.find((v) => {
                if (Array.isArray(v.options)) {
                  return v.options.some((opt) => opt && opt.trim().toLowerCase() === targetVal);
                }
                return [v.option1, v.option2, v.option3].some((opt) => opt && opt.trim().toLowerCase() === targetVal);
              });
            }
            if (matchedVariant) {
              selectedVariantId = matchedVariant.id;
            }
          } catch (e) {
            console.warn('Failed to resolve variant from variants list', e);
          }
        }

        if (selectedVariantId && !shouldSwapProduct) {
          this.updateURL(productUrl, selectedVariantId);
          this.updateVariantInputs(selectedVariantId);
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, selectedVariantId),
          targetId: target.id,
          shouldFocus: !preventMediaScroll,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode, { preventMediaScroll, selectedVariantId, matchedVariant }),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      resetProductFormState() {
        try {
          const productForm = this.productForm;
          productForm?.resetFormState();
        } catch (e) {
          console.warn("Failed to reset product form state:", e);
        }
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
          if (variant) {
            document.dispatchEvent(
              new CustomEvent('variant:changed', {
                detail: { variant },
              }),
            );
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          }
        };
      }

      handleUpdateProductInfo(productUrl, viewMode, { preventMediaScroll = false, selectedVariantId = null, matchedVariant = null } = {}) {
        return (html) => {
          let variant = this.getSelectedVariant(html);

          if (!variant || (selectedVariantId && String(variant.id) !== String(selectedVariantId))) {
            if (matchedVariant && (!selectedVariantId || String(matchedVariant.id) === String(selectedVariantId))) {
              variant = matchedVariant;
            } else if (selectedVariantId) {
              const variantsEl =
                this.querySelector('variant-selects [data-product-variants]') ||
                document.querySelector('[data-product-variants], [data-sticky-atc-variants]');
              if (variantsEl?.textContent) {
                try {
                  const allVariants = JSON.parse(variantsEl.textContent);
                  const found = allVariants.find((v) => String(v.id) === String(selectedVariantId));
                  if (found) variant = found;
                } catch (e) {}
              }
            }
          }

          this.currentVariant = variant;
          const currentVariantId = variant?.id || selectedVariantId;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, currentVariantId);
          this.updateShareUrl(currentVariantId);
          this.updateVariantInputs(currentVariantId);

          if (!variant) {
            const formContainer = this.querySelector('.product-form') || html.querySelector('.product-form');
            const isWaitlistEnabled =
              formContainer?.getAttribute('data-enable-waitlist') === 'true' ||
              this.querySelector('[data-waitlist-configured="true"]') !== null;

            if (isWaitlistEnabled) {
              this.setUnavailable(false);
              this.currentVariant = null;

              const defaultPopupLink =
                formContainer?.getAttribute('data-default-waitlist-popup-link') ||
                this.querySelector('[data-default-waitlist-popup-link]')?.getAttribute('data-default-waitlist-popup-link') ||
                '';
              const defaultWaitlistText =
                formContainer?.getAttribute('data-default-waitlist-text') ||
                this.querySelector('[data-default-waitlist-text]')?.getAttribute('data-default-waitlist-text') ||
                FoxTheme.variantStrings.joinWaitlist ||
                'JOIN THE WAITLIST';
              const defaultModalId =
                formContainer?.getAttribute('data-default-waitlist-modal-id') ||
                this.querySelector('[data-default-waitlist-modal-id]')?.getAttribute('data-default-waitlist-modal-id') ||
                '';
              const defaultCustomJs =
                formContainer?.getAttribute('data-default-waitlist-custom-js') ||
                this.querySelector('[data-default-waitlist-custom-js]')?.getAttribute('data-default-waitlist-custom-js') ||
                '';

              const waitlistAttributes = {
                triggerSelector: defaultPopupLink,
                popupLink: defaultPopupLink,
                modalId: defaultModalId,
                customJs: defaultCustomJs,
                variantId: '',
                variantTitle: '',
                productId: this.productId || '',
                productTitle: this.dataset.productTitle || '',
                waitlistText: defaultWaitlistText,
              };

              this.toggleAddButton(false, defaultWaitlistText, true, true, waitlistAttributes);
            } else {
              this.setUnavailable(true);
              this.currentVariant = null;
            }

            document.dispatchEvent(
              new CustomEvent('variant:changed', {
                detail: {
                  variant: null,
                },
              }),
            );
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant: null,
              },
            });
            return;
          }

          this.updateMedia(variant, { preventScroll: preventMediaScroll });

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.querySelector(`#${id}-${this.sectionId}`);
            const destinations = this.querySelectorAll(`#${id}-${this.dataset.section}`);
            if (source) {
              destinations.forEach((destination) => {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              });
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.updateTotalPrice();
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const formContainer = html.querySelector('.product-form') || this.querySelector('.product-form');
          const isWaitlistEnabled =
            formContainer?.getAttribute('data-enable-waitlist') === 'true' ||
            addButtonUpdated?.getAttribute('data-waitlist-configured') === 'true' ||
            this.querySelector('[data-waitlist-configured="true"]') !== null;

          const isAvailable = variant && variant.available === true;
          const isWaitlist = !isAvailable && isWaitlistEnabled;

          const defaultPopupLink =
            formContainer?.getAttribute('data-default-waitlist-popup-link') ||
            addButtonUpdated?.getAttribute('data-default-waitlist-popup-link') ||
            this.querySelector('[data-default-waitlist-popup-link]')?.getAttribute('data-default-waitlist-popup-link') ||
            '';
          const defaultWaitlistText =
            formContainer?.getAttribute('data-default-waitlist-text') ||
            addButtonUpdated?.getAttribute('data-default-waitlist-text') ||
            this.querySelector('[data-default-waitlist-text]')?.getAttribute('data-default-waitlist-text') ||
            FoxTheme.variantStrings.joinWaitlist ||
            'JOIN THE WAITLIST';
          const defaultModalId =
            formContainer?.getAttribute('data-default-waitlist-modal-id') ||
            addButtonUpdated?.getAttribute('data-default-modal-id') ||
            this.querySelector('[data-default-waitlist-modal-id]')?.getAttribute('data-default-waitlist-modal-id') ||
            '';
          const defaultCustomJs =
            formContainer?.getAttribute('data-default-waitlist-custom-js') ||
            addButtonUpdated?.getAttribute('data-default-custom-js') ||
            this.querySelector('[data-default-waitlist-custom-js]')?.getAttribute('data-default-waitlist-custom-js') ||
            '';

          const waitlistAttributes = isWaitlist
            ? {
                triggerSelector: addButtonUpdated?.getAttribute('data-trigger-selector') || defaultPopupLink,
                popupLink: addButtonUpdated?.getAttribute('data-waitlist-popup-link') || defaultPopupLink,
                modalId: addButtonUpdated?.getAttribute('data-modal-id') || defaultModalId,
                customJs: addButtonUpdated?.getAttribute('data-custom-js') || defaultCustomJs,
                variantId: variant?.id || '',
                variantTitle: variant?.title || '',
                productId: this.productId || '',
                productTitle: this.dataset.productTitle || '',
                waitlistText:
                  addButtonUpdated?.getAttribute('data-waitlist-text') ||
                  defaultWaitlistText,
              }
            : {};

          if (isWaitlist) {
            this.toggleAddButton(false, waitlistAttributes.waitlistText, true, true, waitlistAttributes);
          } else if (isAvailable) {
            this.toggleAddButton(false, FoxTheme.variantStrings.addToCart, true, false);
          } else {
            this.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              FoxTheme.variantStrings.soldOut,
              true,
              false
            );
          }

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (variantId) {
          params.push(`variant=${variantId}`);
        }

        const validOptionValues = (optionValues || []).filter((val) => val && String(val).trim() !== '' && val !== 'undefined');
        if (validOptionValues.length) {
          params.push(`option_values=${validOptionValues.join(',')}`);
        }

        const cleanUrl = (url || '').split('?')[0].split('#')[0];
        return `${cleanUrl}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant =
          productInfoNode.querySelector('variant-selects [data-selected-variant]')?.textContent ||
          productInfoNode.querySelector('[data-product-selected-variant]')?.textContent;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, shouldFocus = true, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (shouldFocus) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const rawUrl = url || this.dataset.url || window.location.pathname;
        const basePath = rawUrl.split('?')[0].split('#')[0];

        const rawSearch = window.location.search.replace(/\?/g, '&').replace(/^&+/, '?');
        const searchParams = new URLSearchParams(rawSearch);

        for (const key of Array.from(searchParams.keys())) {
          if (key.startsWith('?') || key.startsWith('#')) {
            const cleanKey = key.replace(/^[?#]+/, '');
            const val = searchParams.get(key);
            searchParams.delete(key);
            if (cleanKey && !searchParams.has(cleanKey)) {
              searchParams.set(cleanKey, val);
            }
          }
        }

        if (variantId) {
          searchParams.set('variant', variantId);
        } else {
          searchParams.delete('variant');
        }

        const queryString = searchParams.toString();
        const newUrl = `${basePath}${queryString ? `?${queryString}` : ''}${window.location.hash || ''}`;

        window.history.replaceState({ path: newUrl }, '', newUrl);
        console.log('🔗 [ProductInfo] URL updated to:', newUrl);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });

        // Sticky ATC uses its own product form, so it is not included in the
        // form IDs above. Keep its variant input in sync with the main form.
        // Do not dispatch `change` here: a sticky variant select uses that
        // event to initiate a new variant request.
        document.querySelectorAll('sticky-atc-bar').forEach((stickyAtc) => {
          if (stickyAtc.dataset.productRootId !== this.id) return;

          const input = stickyAtc.querySelector('.sticky-atc-bar__form [name="id"]');
          if (input) input.value = variantId ?? '';
        });
      }

      updateMedia(variant, { preventScroll = false } = {}) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant, { preventScroll });
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        const cleanPath = (this.dataset.url || '').split('?')[0].split('#')[0];
        shareButton.updateUrl(`${window.shopUrl}${cleanPath}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true, isWaitlist = false, waitlistAttributes = {}) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const dynamicCheckout = productForm.querySelector('.shopify-payment-button');
        if (!addButton) return;
        // Resolve the label before variant updates continue to downstream subscribers.
        const addButtonText = addButton.querySelector('.btn__text') || addButton.querySelector('span');

        if (isWaitlist) {
          addButton.removeAttribute('disabled');
          addButton.setAttribute('type', 'button');
          addButton.setAttribute('data-waitlist-trigger', 'true');
          addButton.classList.add('btn--waitlist');
          if (waitlistAttributes.popupLink) {
            addButton.setAttribute('data-waitlist-popup-link', waitlistAttributes.popupLink);
          } else {
            addButton.removeAttribute('data-waitlist-popup-link');
          }
          if (waitlistAttributes.triggerSelector) {
            addButton.setAttribute('data-trigger-selector', waitlistAttributes.triggerSelector);
          } else {
            addButton.removeAttribute('data-trigger-selector');
          }
          if (waitlistAttributes.modalId) {
            addButton.setAttribute('data-modal-id', waitlistAttributes.modalId);
          } else {
            addButton.removeAttribute('data-modal-id');
          }
          if (waitlistAttributes.customJs) {
            addButton.setAttribute('data-custom-js', waitlistAttributes.customJs);
          } else {
            addButton.removeAttribute('data-custom-js');
          }
          if (waitlistAttributes.variantId) {
            addButton.setAttribute('data-variant-id', waitlistAttributes.variantId);
          }
          if (waitlistAttributes.variantTitle) {
            addButton.setAttribute('data-variant-title', waitlistAttributes.variantTitle);
          }
          if (addButtonText) {
            addButtonText.textContent =
              text || waitlistAttributes.waitlistText || FoxTheme.variantStrings.joinWaitlist || 'JOIN THE WAITLIST';
          }
          if (dynamicCheckout) dynamicCheckout.classList.add('hidden');
        } else if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          addButton.setAttribute('type', 'submit');
          addButton.removeAttribute('data-waitlist-trigger');
          addButton.removeAttribute('data-waitlist-popup-link');
          addButton.removeAttribute('data-trigger-selector');
          addButton.removeAttribute('data-modal-id');
          addButton.removeAttribute('data-custom-js');
          addButton.classList.remove('btn--waitlist');
          if (text && addButtonText) addButtonText.textContent = text;
          if (dynamicCheckout) dynamicCheckout.classList.add('hidden');
        } else {
          addButton.removeAttribute('disabled');
          addButton.setAttribute('type', 'submit');
          addButton.removeAttribute('data-waitlist-trigger');
          addButton.removeAttribute('data-waitlist-popup-link');
          addButton.removeAttribute('data-trigger-selector');
          addButton.removeAttribute('data-modal-id');
          addButton.removeAttribute('data-custom-js');
          addButton.classList.remove('btn--waitlist');
          if (addButtonText) addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
          if (dynamicCheckout) dynamicCheckout.classList.remove('hidden');
        }

        if (!modifyClass) return;
      }

      setUnavailable(disableButton = true) {
        if (disableButton) {
          this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        }
        const prices = document.querySelectorAll(`#price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        prices.forEach((price) => price.classList.add('hidden'));
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      handleQuantityChange() {
        this.updateTotalPrice();
      }

      updateTotalPrice() {
        if (!this.currentVariant) return;

        const priceContainers = this.querySelectorAll(`#price-${this.dataset.section}`);
        if (!priceContainers.length) return;

        const quantity = Math.max(Number.parseInt(this.quantityInput?.value, 10) || 1, 1);
        const totalPrice = this.currentVariant.price * quantity;
        const totalCompareAtPrice = this.currentVariant.compare_at_price
          ? this.currentVariant.compare_at_price * quantity
          : null;
        const moneyFormat = FoxTheme.settings.moneyFormat;

        priceContainers.forEach((priceContainer) => {
          priceContainer.querySelectorAll('.f-price').forEach((priceWrapper) => {
            const salePrice = priceWrapper.querySelector('.f-price-item--sale');
            const regularPrices = priceWrapper.querySelectorAll('.f-price-item--regular');
            const unitPriceWrapper = priceWrapper.querySelector('.f-price__unit-wrapper');
            const unitPrice = unitPriceWrapper?.querySelector('.f-price__unit');
            const isOnSale = totalCompareAtPrice && totalCompareAtPrice > totalPrice;

            priceWrapper.classList.toggle('f-price--on-sale', Boolean(isOnSale));
            priceWrapper.classList.toggle('f-price--sold-out', !this.currentVariant.available);

            if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(totalPrice, moneyFormat);

            regularPrices.forEach((item) => {
              item.innerHTML = isOnSale
                ? `<s>${FoxTheme.Currency.formatMoney(totalCompareAtPrice, moneyFormat)}</s>`
                : FoxTheme.Currency.formatMoney(totalPrice, moneyFormat);
            });

            if (this.currentVariant.unit_price_measurement && unitPrice) {
              const measurement = this.currentVariant.unit_price_measurement;
              const baseUnit = `${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit}`;
              unitPriceWrapper.classList.remove('hidden');
              unitPrice.innerHTML = `<span>${FoxTheme.Currency.formatMoney(
                this.currentVariant.unit_price,
                moneyFormat
              )}</span>/<span data-unit-price-base-unit>${baseUnit}</span>`;
            } else {
              unitPriceWrapper?.classList.add('hidden');
            }
          });
        });
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        const previousQuantity = this.quantityInput?.value;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);

            // Quantity rules reset the input value directly after an add-to-cart update.
            // Notify price and sticky-bar listeners when that reset changes the quantity.
            if (this.quantityInput && this.quantityInput.value !== previousQuantity) {
              this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}

/* === Add price block installment shadowroot element atc button a arrow === B */
(() => {
  const ctaSelector = '#shopify-installments-cta';
  const arrowAttribute = 'data-installments-cta-arrow';
  const observedRoots = new WeakSet();

  const decorateCta = (cta) => {
    cta.style.position = 'relative';
    cta.style.textDecorationLine = 'none';

    if (cta.querySelector(`[${arrowAttribute}]`)) return;

    const arrow = document.createElement('span');
    arrow.setAttribute(arrowAttribute, '');
    arrow.setAttribute('aria-hidden', 'true');
    Object.assign(arrow.style, {
      position: 'absolute',
      top: '50%',
      right: '-16px',
      transform: 'translateY(-50%)',
      display: 'flex',
      width: '12px',
      height: '12px',
      background:
        'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M4 2L8 6L4 10\' stroke=\'black\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") center / 12px 12px no-repeat',
    });
    cta.append(arrow);
  };

  const observeRoot = (root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);

    root.querySelectorAll(ctaSelector).forEach(decorateCta);
    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) observeRoot(element.shadowRoot);
    });

    new MutationObserver(() => {
      root.querySelectorAll(ctaSelector).forEach(decorateCta);
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) observeRoot(element.shadowRoot);
      });
    }).observe(root, { childList: true, subtree: true });
  };

  observeRoot(document);
})();

/* === Add price block installment shadowroot element atc button a arrow === E */
